import React, { useEffect, useRef, useState } from 'react';
import type { Currency } from '@hero/schema';
import { formatNumberWithCommas, roundPrice } from '@hero/core';
import { cn } from '@hero/web-lib';
import { Input } from '../../ui/Input/Input';
import { CurrencySymbol } from '../Price/Price';

/**
 * Editable money field. Pairs with the read-only `<Price />` for the
 * "right side" / display surfaces — same visual language (currency
 * glyph + tabular numbers + comma grouping) but accepts user input.
 *
 * Designed for the order-pricing UIs where we already use `<Price />`
 * to display: keeping a matching input avoids the visual mismatch
 * between "view price" and "edit price" the dashboard had before.
 *
 * Behaviour:
 *
 *   - Currency glyph (SAR uses the riyal glyph via `<CurrencySymbol/>`)
 *     sits on the leading edge inside the input, NOT outside, so the
 *     control reads as a single unit even in dense forms.
 *   - The user types digits; we format with commas as they go and
 *     allow at most two decimal digits.
 *   - On blur we round to the configured precision via `roundPrice`.
 *   - `priceType` adds an inline hint below ("Before tax" / "Includes
 *     tax") so consumers don't need to repeat the label.
 *
 * Note for form usage: pass `value` and `onChange(numberOrUndefined)`.
 * `undefined` means "user cleared the field"; `0` is a real zero.
 */
export type PriceType = 'before_tax' | 'after_tax' | 'any';

export interface PriceInputProps {
  value: number | undefined;
  onChange: (next: number | undefined) => void;
  currency?: Currency;
  /** Render an inline hint under the field. */
  priceType?: PriceType;
  /** Override the auto hint when `priceType` is `'any'`. */
  hint?: React.ReactNode;
  /** Optional label rendered above the field. */
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  /** Validation error text. Renders in red below the hint. */
  error?: string;
  className?: string;
  /** Defaults to `'default'`. `'green'` / `'red'` accent the border & glyph. */
  tone?: 'default' | 'green' | 'red';
  /**
   * Optional upper bound on the entered value. When the user types
   * above this ceiling the component clamps to `capAmount` on
   * change and blur — used by payment drawers that must not exceed
   * an invoice's `amount.remaining`.
   */
  capAmount?: number;
}

const TONE_CLASSES: Record<NonNullable<PriceInputProps['tone']>, { wrap: string; glyph: string }> = {
  default: {
    wrap: 'border-input bg-background',
    glyph: 'text-muted-foreground',
  },
  green: {
    wrap: 'border-emerald-200 bg-emerald-50/40',
    glyph: 'text-emerald-600',
  },
  red: {
    wrap: 'border-red-200 bg-red-50/40',
    glyph: 'text-red-600',
  },
};

/** Hint-resolver kept tiny; consumers can override via `hint` prop. */
const defaultHint = (priceType: PriceType): React.ReactNode => {
  if (priceType === 'before_tax') return 'Before tax';
  if (priceType === 'after_tax') return 'Includes tax';
  return null;
};

const groupIntegerPart = (intStr: string): string => {
  if (intStr === '' || intStr === '0') return intStr;
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatInput = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  // Collapse multiple dots into one.
  const firstDot = cleaned.indexOf('.');
  const oneDot =
    firstDot === -1
      ? cleaned
      : cleaned.slice(0, firstDot + 1) +
        cleaned.slice(firstDot + 1).replace(/\./g, '');
  // Cap decimal places to two digits.
  const [intPart, decPart] = oneDot.split('.');
  const cappedDec = decPart !== undefined ? decPart.slice(0, 2) : undefined;
  const grouped = groupIntegerPart(intPart);
  return cappedDec !== undefined ? `${grouped}.${cappedDec}` : grouped;
};

const stringToNumber = (raw: string): number | undefined => {
  if (raw === '' || raw === '.') return undefined;
  const n = parseFloat(raw.replace(/,/g, ''));
  return Number.isFinite(n) ? n : undefined;
};

export const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  currency = 'SAR',
  priceType = 'any',
  hint,
  label,
  placeholder = '0',
  disabled = false,
  error,
  className,
  tone = 'default',
  capAmount,
}) => {
  const [text, setText] = useState<string>('');
  const isTypingRef = useRef(false);

  // Keep the text in sync when the consumer's value changes externally
  // (e.g. drawer re-seeds on open). Skip while the user is mid-keystroke
  // so we don't reformat what they're literally typing.
  useEffect(() => {
    if (isTypingRef.current) return;
    if (value === undefined) {
      setText('');
    } else {
      setText(formatNumberWithCommas(value));
    }
  }, [value]);

  const clampToCap = (n: number | undefined): number | undefined => {
    if (n === undefined) return undefined;
    if (capAmount != null && n > capAmount) return capAmount;
    return n;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isTypingRef.current = true;
    const formatted = formatInput(e.target.value);
    const parsed = clampToCap(stringToNumber(formatted));
    setText(parsed != null ? formatNumberWithCommas(parsed) : formatted);
    onChange(parsed);
    // Release the typing lock on the next macrotask so external syncs
    // resume after the current burst.
    setTimeout(() => {
      isTypingRef.current = false;
    }, 100);
  };

  const handleBlur = () => {
    if (value === undefined || value === 0) return;
    onChange(roundPrice(clampToCap(value) ?? value));
  };

  const tones = TONE_CLASSES[tone];
  const resolvedHint = hint ?? defaultHint(priceType);

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label className="block text-[12px] font-medium text-gray-700">
          {label}
        </label>
      )}
      <div
        className={cn(
          'relative flex items-center rounded-md border focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 transition-shadow',
          tones.wrap,
          disabled && 'opacity-60',
        )}>
        <span
          className={cn(
            'flex items-center ps-3 text-[14px] font-medium tabular-nums select-none',
            tones.glyph,
          )}>
          <CurrencySymbol currency={currency} />
        </span>
        <Input
          type="text"
          inputMode="decimal"
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="border-0 bg-transparent ps-2 pe-3 focus-visible:ring-0 focus-visible:ring-offset-0 tabular-nums"
        />
      </div>
      {(resolvedHint || error) && (
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-gray-500">{resolvedHint}</span>
          {error && <span className="text-red-600">{error}</span>}
        </div>
      )}
    </div>
  );
};
